import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, RotateCcw } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { inputClass } from "../components/FormField";
import { getStoredAccountType } from "../auth";
import { useAdminInvoices } from "../hooks/useAdminInvoices";
import { DeadlineBadge, deadlineText } from "../components/DeadlineBadge";
import { ExportMenu } from "../components/ExportMenu";

function formatAmount(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
}

function getDashboardHomeHref() {
  const accountType = getStoredAccountType();
  if (accountType === "auditor") return "/dashboard/auditor";
  if (accountType === "hospital") return "/dashboard/hospital";
  if (accountType === "enterprise") return "/dashboard/enterprise";
  return "/dashboard/admin";
}

export function RejectedInvoicesPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.invoices.rejected;
  const { exportTable } = usePageActions();
  const { invoices: rejectedInvoicesList, loading, error } = useAdminInvoices({
    mode: "rejected",
  });

  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    return rejectedInvoicesList.filter((invoice) => {
      if (!search) return true;
      const haystack =
        `${invoice.displayId} ${invoice.hospital} ${invoice.company} ${invoice.reference} ${invoice.submittedDate}`.toLowerCase();
      return haystack.includes(search.toLowerCase());
    });
  }, [rejectedInvoicesList, search]);

  const columns = [
    labels.columns.id,
    labels.columns.date,
    labels.columns.hospital,
    "Assureur",
    "Échéance",
    labels.columns.amount,
    labels.columns.status,
    labels.columns.actions,
  ];

  const handleExportCsv = () => {
    exportTable(
      "factures-rejetees.csv",
      columns.slice(0, -1),
      filtered.map((invoice) => [
        String(invoice.displayId),
        invoice.submittedDate,
        invoice.hospital,
        invoice.insurer || invoice.company,
        deadlineText(invoice.deadline),
        formatAmount(invoice.amount),
        labels.status,
      ]),
    );
  };

  return (
    <>
      <Breadcrumbs
        homeHref={getDashboardHomeHref()}
        items={[
          { label: t.safecheck.nav.invoices, href: "/dashboard/invoices" },
          { label: labels.title },
        ]}
      />

      <div className="mb-6">
        <h1 className="text-xl font-bold text-foreground">{labels.title}</h1>
        {loading ? <p className="mt-1 text-sm text-muted">Chargement…</p> : null}
        {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : null}
      </div>

      <div className="theme-card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={labels.searchPlaceholder}
            className={`${inputClass} w-full min-w-[200px] max-w-sm`}
          />
          <div className="flex items-center gap-2">
            <ExportMenu onExport={handleExportCsv} />
            <button
              type="button"
              onClick={() => setSearch("")}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent"
              aria-label={t.safecheck.reset}
            >
              <RotateCcw size={15} />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="safecheck-table w-full min-w-[960px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {columns.map((header) => (
                  <th
                    key={header}
                    className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((invoice) => (
                <tr
                  key={invoice.id}
                  className="border-b border-border last:border-0 hover:bg-card-muted/30"
                >
                  <td className="px-4 py-3 text-sm font-medium text-foreground">
                    <Link
                      to={`/dashboard/invoices/${invoice.displayId}`}
                      className="text-foreground transition-colors hover:text-primary"
                    >
                      {invoice.displayId}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{invoice.submittedDate}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{invoice.hospital}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{invoice.insurer || invoice.company}</td>
                  <td className="px-4 py-3">
                    <DeadlineBadge deadline={invoice.deadline} />
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-foreground">
                    {formatAmount(invoice.amount)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center rounded-md bg-danger-soft px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-danger">
                      {labels.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/dashboard/invoices/${invoice.displayId}`}
                      aria-label={labels.actions.view}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-primary transition-colors hover:border-accent hover:bg-accent/20"
                    >
                      <Eye size={16} />
                    </Link>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-16 text-center text-sm text-muted">
                    {labels.empty}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
